import React, { useState } from 'react'
import { css, StyleSheet } from 'aphrodite'
import { saveAs } from 'file-saver'
import { AlignmentType,
    BorderStyle,
    Document,
    HeadingLevel,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    WidthType } from 'docx'
import Layout from '../../components/Layout'
import { useGetRequest, useInfiniteScroll, useLoad } from '../../hooks/request'
import { useQueryParams } from '../../hooks/queryString'
import Button from '../../components/common/Button'
import { useModal } from '../../hooks/modal'
import MarketCreate from '../settings/market/MarketCreate'
import RentItem from './RentItem'
import { format } from '../../utils/number'
import Loader from '../../components/common/Loader'
import { TOTAL_EXPENSE_LIST } from '../../urls'

export default function Rent() {
    const [filters, setFilters] = useState({
        expense_date_month: '',
        expense_date_year: new Date().getFullYear()
    })
    const [showFilters, setShowFilters] = useState(false)

    const params = useQueryParams()
    const markets = useInfiniteScroll(
        {
            url: '/main/market',
            params: { ...params, ...filters, size: 16, page_status: true },
        },
        [params, filters],
    )
    const market1s = useInfiniteScroll(
        {
            url: '/main/market',
            params: { ...params, ...filters, size: 1000, page_status: true },
        },
        [params, filters],
    )
    const summary = useLoad({
        url: '/main/market/summary',
        params: { ...filters }
    }, [filters])

    const totalCost = useLoad({
        url: '/main/total-cost',
    })
    const [showCreateModal, hideCreateModal] = useModal(
        <MarketCreate
            onSuccess={() => {
                hideCreateModal()
                markets.request()
            }}
            onCancel={() => hideCreateModal()}
        />,
    )

    const item = markets.response ? markets.response.results : []
    const calc = useGetRequest({
        url: '/main/calc',
    })
    const downloadMarketData = async () => {
        if (!market1s.response || !market1s.response.results.length) {
            alert('Нет данных для экспорта')
            return
        }

        // Get current month and year in Russian
        const date = new Date()
        const currentYear = date.getFullYear()
        const russianMonths = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
        const currentMonth = russianMonths[date.getMonth()]

        // Format date as DD/MM/YY for footer
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const shortYear = date.getFullYear().toString().substr(-2)
        const formattedDate = `${day}/${month}/${shortYear}`

        // Create header row
        const headerRow = new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph({
                            text: '№',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Дўкон номи',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Ойлик Ижара суммаси',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Ойлик Камунал',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Кирим',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Қарздорлик',
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
            ],
        })

        // Calculate totals
        let totalUtilities = 0
        let totalIncome = 0
        let totalBalance = 0
        let totalPrice = 0

        market1s.response.results.forEach((market) => {
            // Fix field names to match backend response
            totalPrice += parseFloat(market.price || 0)
            totalUtilities += parseFloat(market.totalUtilities || 0)
            totalIncome += parseFloat(market.totalIncome || 0)
            totalBalance += parseFloat(market.totalBalance || 0)
        })

        // Create data rows
        const rows = market1s.response.results.map(
            (market, index) => new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: (index + 1).toString(),
                                alignment: AlignmentType.CENTER
                            })
                        ]
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: market.name })]
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: format(market.price || 0),
                                alignment: AlignmentType.RIGHT
                            })
                        ]
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: format(market.totalUtilities || 0),
                                alignment: AlignmentType.RIGHT
                            })
                        ]
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: format(market.totalIncome || 0),
                                alignment: AlignmentType.RIGHT
                            })
                        ]
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: format(market.totalBalance || 0),
                                alignment: AlignmentType.RIGHT
                            })
                        ]
                    })
                ]
            })
        )

        // Add totals row
        const totalRow = new TableRow({
            children: [
                new TableCell({
                    children: [
                        new Paragraph({
                            text: '   ',
                            alignment: AlignmentType.LEFT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: 'Жами',
                            alignment: AlignmentType.LEFT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: `Жами Ойлик Ижара: ${format(totalPrice)}`,
                            alignment: AlignmentType.RIGHT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: `Жами Ойлик Камунал: ${format(totalUtilities)}`,
                            alignment: AlignmentType.RIGHT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: `Жами Кирим: ${format(totalIncome)}`,
                            alignment: AlignmentType.RIGHT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
                new TableCell({
                    children: [
                        new Paragraph({
                            text: `Жами Қарздорлик: ${format(totalBalance)}`,
                            alignment: AlignmentType.RIGHT,
                            bold: true,
                        }),
                    ],
                    shading: { fill: 'EEEEEE' },
                }),
            ],
        })

        // Create table with all rows
        const table = new Table({
            rows: [headerRow, ...rows, totalRow],
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                insideVertical: { style: BorderStyle.SINGLE, size: 1 },
            },
        })

        // Create document with title and table
        const doc = new Document({
            sections: [
                {
                    children: [
                        new Paragraph({
                            text: 'Дўконлар бўйича ҳисобот',
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                        }),
                        new Paragraph({
                            text: `${currentMonth} ${currentYear}`,
                            alignment: AlignmentType.CENTER,
                        }),
                        new Paragraph({ text: '' }),
                        table,
                        new Paragraph({ text: '' }),
                        new Paragraph({
                            text: formattedDate,
                            alignment: AlignmentType.RIGHT,
                        }),
                    ],
                },
            ],
        })

        // Save document
        const blob = await Packer.toBlob(doc)
        saveAs(blob, 'markets_report.docx')
    }
    // Add this function after downloadMarketData function
    const downloadUtilityCostsData = async () => {
        if (!totalCost.response || !totalCost.response.results.length) {
            alert('Нет данных для экспорта коммунальных платежей')
            return
        }

        // Get current date info for document
        const date = new Date()
        const currentYear = date.getFullYear()
        const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear().toString().substr(-2)}`

        // Create header row with months
        const headerCells = [
            new TableCell({
                children: [
                    new Paragraph({
                        text: 'Дўкон номи',
                        heading: HeadingLevel.HEADING_4,
                        alignment: AlignmentType.CENTER
                    })
                ],
                shading: { fill: 'CCCCCC' },
            }),
        ]

        // Add all months to header
        const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

        months.forEach((month) => {
            headerCells.push(
                new TableCell({
                    children: [
                        new Paragraph({
                            text: month,
                            heading: HeadingLevel.HEADING_4,
                            alignment: AlignmentType.CENTER
                        })
                    ],
                    shading: { fill: 'CCCCCC' },
                }),
            )
        })

        const headerRow = new TableRow({ children: headerCells })

        // Create data rows for each market
        const rows = totalCost.response.results.map((market) => {
            const cells = [
                new TableCell({
                    children: [new Paragraph({ text: market.name })],
                }),
            ]

            // Add cell for each month with the cost
            market.monthlyCosts.forEach((monthData) => {
                cells.push(
                    new TableCell({
                        children: [
                            new Paragraph({
                                text: format(monthData.totalCost || 0),
                                alignment: AlignmentType.RIGHT
                            })
                        ]
                    }),
                )
            })

            return new TableRow({ children: cells })
        })

        // Create table with all rows
        const table = new Table({
            rows: [headerRow, ...rows],
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                insideVertical: { style: BorderStyle.SINGLE, size: 1 },
            },
        })

        // Create document
        const doc = new Document({
            sections: [
                {
                    children: [
                        new Paragraph({
                            text: 'Коммунал тўловлар ҳисоботи',
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER
                        }),
                        new Paragraph({
                            text: `${currentYear}`,
                            alignment: AlignmentType.CENTER
                        }),
                        new Paragraph({ text: '' }),
                        table,
                        new Paragraph({ text: '' }),
                        new Paragraph({
                            text: formattedDate,
                            alignment: AlignmentType.RIGHT
                        })
                    ]
                }
            ]
        })

        // Save document
        const blob = await Packer.toBlob(doc)
        saveAs(blob, 'utility_costs_report.docx')
    }

    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const clearFilters = () => {
        setFilters({
            expense_date_month: '',
            expense_date_year: new Date().getFullYear()
        })
    }

    const months = [
        { value: 1, label: 'Янvar' },
        { value: 2, label: 'Fevral' },
        { value: 3, label: 'Mart' },
        { value: 4, label: 'Aprel' },
        { value: 5, label: 'May' },
        { value: 6, label: 'Iyun' },
        { value: 7, label: 'Iyul' },
        { value: 8, label: 'Avgust' },
        { value: 9, label: 'Sentabr' },
        { value: 10, label: 'Oktabr' },
        { value: 11, label: 'Noyabr' },
        { value: 12, label: 'Dekabr' }
    ]

    const expense = useLoad(
        {
            url: TOTAL_EXPENSE_LIST,
            params: { ...filters, ...params, space_type: 'all' }
        },
        [filters, params]
    )

    return (
        <Layout>
            <div className="is-flex is-flex-direction-row is-flex-direction-column-mobile is-justify-content-space-between is-align-items-center mb-3">
                <h1 className="title mb-3-mobile">Дўконлар</h1>
                <div className="is-flex is-flex-wrap-wrap">
                    <Button text={showFilters ? 'Фильтрни ёпиш' : 'Фильтр'}
                        className="button is-info is-outlined mr-2 mb-2-mobile"
                        onClick={() => setShowFilters(!showFilters)} />
                    <Button text="Коммунал ҳисоботи" className="button is-success is-outlined mr-2 mb-2-mobile" onClick={downloadUtilityCostsData} />
                    <Button text="Янгилаш" className="button is-info is-outlined mr-2 mb-2-mobile" onClick={calc.request} />
                    <Button text="Ҳисоботни юклаш" className="button is-info is-outlined mr-2 mb-2-mobile" onClick={downloadMarketData} />
                    <Button text="Қўшиш" className="button is-link is-outlined" onClick={showCreateModal} />
                </div>
            </div>

            {/* Filter Section */}
            {showFilters && (
                <div className="box mb-4">
                    <h3 className="subtitle is-5 mb-3">Харажат санаси бўйича фильтр</h3>

                    <div className="columns">
                        <div className="column is-4">
                            <label className="label">Ой</label>
                            <div className="select is-fullwidth">
                                <select value={filters.expense_date_month}
                                    onChange={(e) => handleFilterChange('expense_date_month', e.target.value)}>
                                    <option value="">Барча ойлар</option>
                                    {months.map((month) => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="column is-4">
                            <label className="label">Йил</label>
                            <input className="input" type="number" value={filters.expense_date_year}
                                onChange={(e) => handleFilterChange('expense_date_year', e.target.value)}
                                placeholder="Йилни киритинг" />
                        </div>

                        <div className="column is-4">
                            <label className="label">&nbsp;</label>
                            <div className="field">
                                <Button text="Фильтрни тозалаш" className="button is-light" onClick={clearFilters} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="columns is-mobile is-multiline">
                <div className="column is-3-desktop is-6-tablet is-12-mobile">
                    <div
                        style={{
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#f0fff4',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        <p className="has-text-success has-text-centered">
                            <span className="icon-text">
                                <ion-icon name="arrow-down-outline" />
                                <b className="ml-2">Кирим:</b>
                            </span>
                            <span className="ml-2 has-text-weight-bold">{format(summary.response ? summary.response.totalIncomeSum : 0)} UZS</span>
                        </p>
                    </div>
                </div>

                <div className="column is-3-desktop is-6-tablet is-12-mobile">
                    <div
                        style={{
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#fff5f5',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        <p className="has-text-danger has-text-centered">
                            <span className="icon-text">
                                <ion-icon name="arrow-up-outline" />
                                <b className="ml-2">Чиқим:</b>
                            </span>
                            <span className="ml-2 has-text-weight-bold">{format(expense.response ? expense.response.arendaTotal : 0)} UZS</span>
                        </p>
                    </div>
                </div>

                <div className="column is-3-desktop is-6-tablet is-12-mobile">
                    <div
                        style={{
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#f0f8ff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        <p className="has-text-link has-text-centered">
                            <span className="icon-text">
                                <ion-icon name="flash-outline" />
                                <b className="ml-2">Коммунал:</b>
                            </span>
                            <span className="ml-2 has-text-weight-bold">{format(summary.response ? summary.response.utilityTotalSum : 0)} UZS</span>
                        </p>
                    </div>
                </div>

                <div className="column is-3-desktop is-6-tablet is-12-mobile">
                    <div
                        style={{
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: '#f0f7ff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        <p className="has-text-info has-text-centered">
                            <span className="icon-text">
                                <ion-icon name="wallet-outline" />
                                <b className="ml-2">Баланс:</b>
                            </span>
                            <span className="ml-2 has-text-weight-bold">{format(summary.response ? summary.response.totalBalanceSum : 0)} UZS</span>
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <div className="">
                    <div className="columns is-multiline">
                        {item.map((market, index) => (
                            <RentItem markets={markets} market={market} index={index} />
                        ))}
                    </div>

                    <div ref={markets.ref} className="has-text-grey-light is-italic has-text-centered">
                        {!markets.hasMore && !markets.loading && markets.length !== 0 ? 'Барча дўконлар юкланган' : ''}
                    </div>
                </div>
                {markets.loading ? <Loader className={css(styles.loader)} large center /> : null}
            </div>
        </Layout>
    )
}

const styles = StyleSheet.create({
    space: { marginTop: '2rem' },
    emptyImg: { width: '330px', height: '300px' },
    loader: { marginTop: '2rem' },
    modal: { width: 1000 },

    box: {
        borderWidth: 2,
        borderColor: 'rgba(255, 99, 71, 0.5)',
        borderRadius: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 3,
        padding: 10,
    },

    boxHover: {
        borderColor: 'rgba(255, 69, 0, 0.8)',
        shadowOpacity: 0.2,
        elevation: 5,
    },
})
